import { defineStore } from 'pinia'
import { api, extractError } from '@/api/client'
import type {
  ActiveSwitchEntry,
  ActiveSwitchTest,
  SwitchActionResponse,
  SwitchOptionsResponse,
  SwitchPollResponse,
} from '@/types'
import { useKeysStore } from '@/stores/keys'
import { useUserStore } from '@/stores/user'

function resolveSwitchGroupKey(keyId: number, data?: { group_key?: string; hash?: string }, userId?: number): string {
  if (data?.group_key) return data.group_key
  const key = useKeysStore().items.find((k) => k.id === keyId)
  if (key?.group_key) return key.group_key
  if (data?.hash && userId) return `single-${userId}-${data.hash}`
  return `id:${keyId}`
}

interface State {
  activeTests: Record<string, ActiveSwitchTest>
  hungMessages: Record<string, string>
  pollTimer: ReturnType<typeof setInterval> | null
  confirming: Record<number, boolean>
  cancelling: Record<number, boolean>
}

function resolveExpiresMs(data: {
  seconds_left?: number
  test_expires_at?: number
  hash_expires_at?: number
  server_time?: number
}): number {
  if (typeof data.seconds_left === 'number' && data.seconds_left > 0) {
    return Date.now() + data.seconds_left * 1000
  }
  const expSec = data.test_expires_at ?? data.hash_expires_at
  if (typeof expSec === 'number' && expSec > 0) {
    let ms = expSec < 1e12 ? expSec * 1000 : expSec
    if (data.server_time) {
      const drift = Date.now() - data.server_time * 1000
      if (Math.abs(drift) > 30_000) ms -= drift
    }
    return ms
  }
  return Date.now() + 60 * 60 * 1000
}

function applyActiveFields(
  target: ActiveSwitchTest,
  data: {
    switch_status?: string
    status?: string
    old_server_id?: number
    new_server_id?: number
    old_server_name?: string
    new_server_name?: string
    old_server_location?: string
    new_server_location?: string
    key_id?: number
    hash?: string
    group_key?: string
  },
): void {
  const status = data.switch_status ?? data.status
  if (status) target.status = status
  if (typeof data.old_server_id === 'number') target.oldServerId = data.old_server_id
  if (typeof data.new_server_id === 'number') target.newServerId = data.new_server_id
  if (data.old_server_name) target.oldServerName = data.old_server_name
  if (data.new_server_name) target.newServerName = data.new_server_name
  if (data.old_server_location) target.oldServerLocation = data.old_server_location
  if (data.new_server_location) target.newServerLocation = data.new_server_location
  if (typeof data.key_id === 'number' && data.key_id > 0) target.switchKeyId = data.key_id
  if (data.group_key) target.switchGroupKey = data.group_key
}

function entryToTest(entry: ActiveSwitchEntry, fallbackKeyId = 0): ActiveSwitchTest {
  const userId = useUserStore().profile?.id
  const switchKeyId = entry.key_id || fallbackKeyId
  const test: ActiveSwitchTest = {
    switchId: entry.switch_id,
    switchKeyId,
    switchGroupKey: resolveSwitchGroupKey(switchKeyId, entry, userId),
    status: entry.status,
    expiresAtMs: resolveExpiresMs(entry),
    oldServerId: entry.old_server_id ?? 0,
    newServerId: entry.new_server_id ?? 0,
    oldServerName: entry.old_server_name ?? '',
    newServerName: entry.new_server_name ?? '',
    oldServerLocation: entry.old_server_location ?? '',
    newServerLocation: entry.new_server_location ?? '',
  }
  applyActiveFields(test, entry)
  return test
}

export const useSwitchStore = defineStore('switch', {
  state: (): State => ({
    activeTests: {},
    hungMessages: {},
    pollTimer: null,
    confirming: {},
    cancelling: {},
  }),

  getters: {
    activeTestList(state): ActiveSwitchTest[] {
      return Object.values(state.activeTests)
    },
    hasActiveTest(state): boolean {
      return Object.keys(state.activeTests).length > 0
    },
    activeTest(state): ActiveSwitchTest | null {
      const vals = Object.values(state.activeTests)
      return vals[0] ?? null
    },
    hungMessage(state): string | null {
      const vals = Object.values(state.hungMessages)
      return vals[0] ?? null
    },
    testForGroup: (state) => (groupKey: string): ActiveSwitchTest | null => {
      return state.activeTests[groupKey] ?? null
    },
    secondsLeftFor: (state) => (groupKey: string): number => {
      const test = state.activeTests[groupKey]
      if (!test) return 0
      return Math.max(0, Math.floor((test.expiresAtMs - Date.now()) / 1000))
    },
    secondsLeft(): number {
      const first = this.activeTest
      if (!first) return 0
      return Math.max(0, Math.floor((first.expiresAtMs - Date.now()) / 1000))
    },
  },

  actions: {
    upsertTest(test: ActiveSwitchTest): void {
      const key = test.switchGroupKey || `id:${test.switchKeyId}`
      this.activeTests = { ...this.activeTests, [key]: test }
    },

    setActiveFromOptions(keyId: number, data: SwitchOptionsResponse): void {
      if (!data.has_active_test || !data.switch_id) return
      const userId = useUserStore().profile?.id
      const test: ActiveSwitchTest = {
        switchId: data.switch_id,
        switchKeyId: keyId,
        switchGroupKey: resolveSwitchGroupKey(keyId, data, userId),
        status: data.switch_status ?? 'testing',
        expiresAtMs: resolveExpiresMs(data),
        oldServerId: data.old_server_id ?? 0,
        newServerId: data.new_server_id ?? 0,
        oldServerName: data.old_server_name ?? '',
        newServerName: data.new_server_name ?? '',
        oldServerLocation: data.old_server_location ?? '',
        newServerLocation: data.new_server_location ?? '',
      }
      applyActiveFields(test, data)
      this.upsertTest(test)
      const gk = test.switchGroupKey || `id:${keyId}`
      const nextHung = { ...this.hungMessages }
      delete nextHung[gk]
      this.hungMessages = nextHung
      this.startPolling()
    },

    setActiveFromPoll(data: SwitchPollResponse, keyId = 0): void {
      if (Array.isArray(data.active_switches)) {
        const next: Record<string, ActiveSwitchTest> = {}
        const hung: Record<string, string> = {}
        for (const entry of data.active_switches) {
          if (!entry || entry.status === 'confirmed' || entry.status === 'cancelled') continue
          const test = entryToTest(entry, keyId)
          const gk = test.switchGroupKey || `id:${test.switchKeyId}`
          if (entry.status === 'hung' || entry.hung_message) {
            hung[gk] = entry.hung_message ?? 'Смена пошла не по плану. Обратитесь в поддержку.'
          }
          next[gk] = test
        }
        this.activeTests = next
        this.hungMessages = hung
        if (Object.keys(next).length > 0) {
          if (!this.pollTimer) this.startPolling()
        } else {
          this.stopPolling()
        }
        return
      }

      const status = data.status ?? data.switch_status ?? ''
      if (status === 'confirmed' || status === 'cancelled') {
        this.clearActive()
        return
      }
      if (!data.has_active_switch && !data.has_active_test) {
        this.clearActive()
        return
      }
      const userId = useUserStore().profile?.id
      const switchKeyId = keyId || data.key_id || 0
      const test: ActiveSwitchTest = {
        switchId: data.switch_id ?? 0,
        switchKeyId,
        switchGroupKey: resolveSwitchGroupKey(switchKeyId, data, userId),
        status,
        expiresAtMs: resolveExpiresMs(data),
        oldServerId: data.old_server_id ?? 0,
        newServerId: data.new_server_id ?? 0,
        oldServerName: data.old_server_name ?? '',
        newServerName: data.new_server_name ?? '',
        oldServerLocation: data.old_server_location ?? '',
        newServerLocation: data.new_server_location ?? '',
      }
      applyActiveFields(test, data)
      this.upsertTest(test)
      const gk = test.switchGroupKey || `id:${switchKeyId}`
      if (status === 'hung') {
        this.hungMessages = {
          ...this.hungMessages,
          [gk]: data.hung_message ?? 'Смена пошла не по плану. Обратитесь в поддержку.',
        }
      }
      if (!this.pollTimer) this.startPolling()
    },

    async poll(): Promise<void> {
      try {
        const { data } = await api.patch<SwitchPollResponse>('/api/user/key-switch', {})
        if (!data.success) return
        this.setActiveFromPoll(data)
        const finished = (data.status === 'confirmed' || data.status === 'cancelled')
          && (!data.active_switches || data.active_switches.length === 0)
        if (finished) {
          await useKeysStore().fetch(true)
          await useUserStore().fetch(true)
        }
      } catch {
        /* ignore transient poll errors */
      }
    },

    startPolling(): void {
      this.stopPolling()
      void this.poll()
      this.scheduleNextPoll()
    },

    scheduleNextPoll(): void {
      this.stopPolling()
      const tests = Object.values(this.activeTests)
      const secs = tests.map((t) => Math.max(0, Math.floor((t.expiresAtMs - Date.now()) / 1000)))
      const sec = secs.length ? Math.min(...secs) : 0
      let intervalMs: number
      if (tests.length > 0 && sec <= 0) {
        intervalMs = 15_000
      } else if (sec > 0 && sec <= 120) {
        intervalMs = 15_000
      } else if (sec > 0 && sec <= 600) {
        intervalMs = 60_000
      } else {
        intervalMs = 5 * 60 * 1000
      }
      this.pollTimer = setInterval(() => {
        void this.poll().then(() => this.scheduleNextPoll())
      }, intervalMs)
    },

    stopPolling(): void {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    clearActive(groupKey?: string): void {
      if (!groupKey) {
        this.activeTests = {}
        this.hungMessages = {}
        this.stopPolling()
        return
      }
      const next = { ...this.activeTests }
      delete next[groupKey]
      this.activeTests = next
      const hung = { ...this.hungMessages }
      delete hung[groupKey]
      this.hungMessages = hung
      if (Object.keys(next).length === 0) this.stopPolling()
    },

    async loadActiveForKey(keyId: number): Promise<void> {
      try {
        const { data } = await api.get<SwitchOptionsResponse>(
          `/api/user/key-switch?key_id=${keyId}`,
        )
        if (data.success && data.has_active_test) {
          this.setActiveFromOptions(keyId, data)
        }
      } catch {
        /* key may not support switch */
      }
    },

    async fetchOptions(keyId: number): Promise<SwitchOptionsResponse> {
      const { data } = await api.get<SwitchOptionsResponse>(
        `/api/user/key-switch?key_id=${keyId}`,
      )
      return data
    },

    async startSwitch(keyId: number, serverId: number): Promise<SwitchActionResponse> {
      const { data } = await api.post<SwitchActionResponse>('/api/user/key-switch', {
        key_id: keyId,
        server_id: serverId,
      })
      if (data.success && data.switch_id) {
        this.setActiveFromOptions(keyId, {
          success: true,
          has_active_test: true,
          switch_id: data.switch_id,
          switch_status: 'testing',
          test_expires_at: data.test_expires_at,
          seconds_left: 3600,
          old_server_name: data.old_server_name,
          new_server_name: data.new_server_name,
          old_server_location: data.old_server_location,
          new_server_location: data.new_server_location,
        })
        await useKeysStore().fetch(true)
        await useUserStore().fetch(true)
      }
      return data
    },

    async confirmSwitch(switchId: number): Promise<{ success: boolean; message?: string }> {
      if (!switchId || this.confirming[switchId]) {
        return { success: false, message: 'Нет активной смены' }
      }
      this.confirming = { ...this.confirming, [switchId]: true }
      try {
        const { data } = await api.put<SwitchActionResponse>('/api/user/key-switch', {
          action: 'confirm',
          switch_id: switchId,
        })
        if (data.success) {
          const gk = Object.keys(this.activeTests).find((k) => this.activeTests[k].switchId === switchId)
          this.clearActive(gk)
          await useKeysStore().fetch(true)
          await useUserStore().fetch(true)
        }
        return data
      } catch (e) {
        return { success: false, message: extractError(e) }
      } finally {
        const next = { ...this.confirming }
        delete next[switchId]
        this.confirming = next
      }
    },

    async cancelSwitch(switchId: number): Promise<{ success: boolean; message?: string }> {
      if (!switchId || this.cancelling[switchId]) {
        return { success: false, message: 'Нет активной смены' }
      }
      this.cancelling = { ...this.cancelling, [switchId]: true }
      try {
        const { data } = await api.put<SwitchActionResponse>('/api/user/key-switch', {
          action: 'cancel',
          switch_id: switchId,
        })
        if (data.success) {
          const gk = Object.keys(this.activeTests).find((k) => this.activeTests[k].switchId === switchId)
          this.clearActive(gk)
          await useKeysStore().fetch(true)
          await useUserStore().fetch(true)
        }
        return data
      } catch (e) {
        return { success: false, message: extractError(e) }
      } finally {
        const next = { ...this.cancelling }
        delete next[switchId]
        this.cancelling = next
      }
    },
  },
})
